import { useEffect, useMemo, useState } from "react";
import useDebouncedCallback from "@restart/hooks/useDebouncedCallback";
import { useLocation } from "react-router-dom";

import {
  ShareProjectSimulatorQuery,
  SharesProject,
  SimulatorScenario,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { formatCurrency } from "@tudigo-monorepo/core-tudigo-utils";
import { useHubspotTrackEvent } from "@tudigo-monorepo/tudigo-session";
import { useGetSimulatorByProject } from "@tudigo-monorepo/web-tudigo-api-client";
import {
  Chip,
  CurrencyInput,
  Icon,
  InputSlider,
  Tooltip,
} from "@tudigo-monorepo/web-tudigo-components";
import {
  HubspotEventEnum,
  useAnalytics,
} from "@tudigo-monorepo/web-tudigo-tracking";

import { InvestButton } from "../../../components/invest-button";
import { ToggleChip } from "../../../components/toggle-chip";
import { useProjectContext } from "../../../hooks/use-project-context";
import { SimulatorPlaygroundBlock } from "../components/simulator-playground-block";
import { SimulatorResultsBlock } from "../components/simulator-results-block";
import { SimulatorRoiBlock } from "../components/simulator-roi-block";
import {
  SHARES_SCENARIOS,
  SIMULATOR_TAX_EXEMPTION_INFOS,
  SimulatorTaxExemptionsEnum,
} from "../simulator-constants";

export function SimulatorShares() {
  const project = useProjectContext() as SharesProject;
  const location = useLocation();
  const analytics = useAnalytics();
  const [isReady, setIsReady] = useState(false);
  const [simulatorForm, setSimulatorForm] =
    useState<ShareProjectSimulatorQuery>({
      amountInCents: 100000,
      taxRate: 0,
    });

  const trackProjectSimulationDone = useHubspotTrackEvent(
    HubspotEventEnum.PROJECT_SIMULATION_DONE,
  );

  const sendTrackEvents = (
    eventType: "amount" | "scenario" | "taxRate",
    value: string | number,
  ) => {
    const mixpanelEventData = {
      project_id: project?.id,
      project_mechanic: project?.mechanic,
      project_simulation_parameter_name: eventType,
      project_simulation_parameter_value: value,
    };

    analytics.track("ui.project_simulator.run", mixpanelEventData);

    if (eventType === "amount") {
      trackProjectSimulationDone?.({
        project_id: project?.id,
        project_slug: project?.content?.slug,
        amount: value.toString(),
      });
    }
  };

  const setFormValue = (key: string, value: string | number) => {
    setSimulatorForm((prev) => ({ ...prev, [key]: value }));
  };

  const { data: simulatorData } = useGetSimulatorByProject(
    {
      projectId: project?.id,
      query: simulatorForm,
    },
    {
      enabled: isReady,
    },
  );

  const filteredTaxExemptions = useMemo(() => {
    if (!project?.taxExemptionMechanics) return null;

    return project?.taxExemptionMechanics?.filter((taxKey) =>
      Object.keys(SimulatorTaxExemptionsEnum).includes(taxKey),
    );
  }, [project]) as SimulatorTaxExemptionsEnum[] | null;

  const handleSliderChange = useDebouncedCallback((e) => {
    const newValue = e?.target?.value * 100;
    setFormValue("amountInCents", newValue);
    sendTrackEvents("amount", newValue / 100);
  }, 150);

  const handleScenarioChange = (newScenario: string) => {
    setFormValue("scenario", newScenario);
    sendTrackEvents("scenario", newScenario);
  };

  const maxValue = project.goal?.maxInCents
    ? project.goal?.maxInCents / 100
    : 2000000;

  const isShareCapitalized = !!project?.sharesMechanicSettings?.buyPack;

  useEffect(() => {
    if (!project) {
      return;
    }
    if (!isShareCapitalized) {
      setFormValue("scenario", SimulatorScenario.Min);
    }
    setFormValue(
      "amountInCents",
      location.state?.amountInCents ??
        project.sharesMechanicSettings?.minimumTicketAmountInCents,
    );

    if (filteredTaxExemptions && filteredTaxExemptions.length > 0) {
      setFormValue(
        "taxRate",
        SIMULATOR_TAX_EXEMPTION_INFOS[filteredTaxExemptions[0]].value,
      );
    }

    setIsReady(true);
  }, [isShareCapitalized, project]);

  const underMinimumErrors = useMemo(() => {
    if (
      simulatorForm?.amountInCents <
      project?.sharesMechanicSettings?.minimumTicketAmountInCents
    ) {
      return [
        `Le montant minimum est de ${formatCurrency(
          project?.sharesMechanicSettings?.minimumTicketAmountInCents,
        )}`,
      ];
    }

    return [];
  }, [
    simulatorForm?.amountInCents,
    project?.sharesMechanicSettings?.minimumTicketAmountInCents,
  ]);

  function getTooltipDesc(): string {
    const scenario = SHARES_SCENARIOS.find(
      (s) => s.value === simulatorForm?.scenario,
    );

    if (!scenario) return "";

    const min =
      (project?.sharesMechanicSettings?.targetedYield?.minInPercent ?? 0) / 100;
    const medium =
      (project?.sharesMechanicSettings?.targetedYield?.mediumInPercent ?? 0) /
      100;
    const max =
      (project?.sharesMechanicSettings?.targetedYield?.maxInPercent ?? 0) / 100;
    const currentScenario = (simulatorData?.targetedYieldInPercent ?? 0) / 100;

    if (!currentScenario) return "";

    return `Potentiel de rendement : ${currentScenario} Les scenarios sont : Conservateur = ${min} / Middle = ${medium} / Management = ${max}. Les performances passées ne préjugent pas des performances futures.`;
  }

  if (!project) return null;

  return (
    <div className="flex flex-col overflow-hidden md:flex-row">
      <SimulatorPlaygroundBlock>
        <article>
          <h2 className="text-primary mb-3 font-bold">{`Montant de l'investissement`}</h2>
          <CurrencyInput
            value={simulatorForm?.amountInCents}
            errors={underMinimumErrors}
            onChange={(number) => {
              setFormValue("amountInCents", number);
              sendTrackEvents("amount", number / 100);
            }}
          />
          <InputSlider
            valueChip={false}
            min={
              project?.sharesMechanicSettings?.minimumTicketAmountInCents / 100
            }
            max={maxValue}
            step={project?.sharesMechanicSettings?.unitPriceInCents / 100}
            className="-mt-[10px]"
            value={simulatorForm.amountInCents / 100}
            onChange={handleSliderChange}
          />
          <footer className="-mt-[15px] flex justify-between">
            <div>Ticket minimum</div>
            <div>
              {formatCurrency(
                project?.sharesMechanicSettings?.minimumTicketAmountInCents,
              )}
            </div>
          </footer>
        </article>
        {!isShareCapitalized && (
          <article>
            <div className="flex">
              <h2 className="text-primary mb-3 font-bold">Scénario</h2>
              <Tooltip description={getTooltipDesc()}>
                <Icon name="Info" size="XS" />
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-2">
              {SHARES_SCENARIOS.map((scenario) => (
                <ToggleChip
                  key={scenario.label}
                  label={scenario.label}
                  value={scenario.value}
                  selectedValue={simulatorForm?.scenario}
                  setSelectedValue={(value) =>
                    handleScenarioChange(value.toString())
                  }
                />
              ))}
            </div>
          </article>
        )}
        <article>
          <div className="flex">
            <h2 className="text-primary mb-3 font-bold">Défiscalisation</h2>
            <Tooltip description="Réduction d'impôts accordée par l'État pour certains investissements. Il appartient à chacun de vérifier son éligibilité.">
              <Icon name="Info" size="XS" />
            </Tooltip>
          </div>
          <div className="flex flex-wrap gap-2">
            <ToggleChip
              label="Aucune"
              value={0}
              selectedValue={simulatorForm.taxRate}
              setSelectedValue={(value) => {
                setFormValue("taxRate", value);
                sendTrackEvents("taxRate", "AUCUNE");
              }}
            />
            {filteredTaxExemptions?.map((taxKey) => (
              <ToggleChip
                key={taxKey}
                label={SIMULATOR_TAX_EXEMPTION_INFOS[taxKey].label}
                value={SIMULATOR_TAX_EXEMPTION_INFOS[taxKey].value}
                selectedValue={simulatorForm.taxRate}
                setSelectedValue={(value) => {
                  setFormValue("taxRate", value);
                  sendTrackEvents("taxRate", taxKey);
                }}
              />
            ))}
          </div>
        </article>
        {project?.riskInPercent !== null && (
          <article>
            <div className="flex">
              <h2 className="text-primary mb-3 font-bold">Risque</h2>
              <Tooltip description="Le potentiel de rendement a pour corolaire un risque de perte totale ou partielle du capital investit. L’analyse financière proposée sur la page projet et dans la note d’investissement détaille le calcul effectué par nos analystes pour chaque levée.">
                <Icon name="Info" size="XS" />
              </Tooltip>
              <div className="ml-4 font-semibold">
                {project?.riskInPercent ? project.riskInPercent / 10 : null} /10
              </div>
            </div>
          </article>
        )}
      </SimulatorPlaygroundBlock>

      <SimulatorResultsBlock>
        <div className="flex justify-between">
          <h2>Montant investi</h2>
          <span className="font-semibold">
            {formatCurrency(simulatorForm.amountInCents)}
          </span>
        </div>
        {simulatorForm?.taxRate !== 0 && (
          <div className="flex justify-between">
            <h2>Économie fiscale potentielle</h2>
            <span className="font-semibold">
              {formatCurrency(Number(simulatorData?.taxExemptionInCents))}
            </span>
          </div>
        )}
        <hr className="border-light-1 border" />

        {isShareCapitalized && (
          <div className="flex items-center justify-between">
            <h2>
              TRI capitalisé sur <span className="font-semibold">4 ans</span>
            </h2>
            <Chip
              label={simulatorData?.tri ? `X${simulatorData?.tri}%` : "-"}
              type="success"
            />
          </div>
        )}

        {!isShareCapitalized && (
          <div className="flex items-center justify-between">
            <h2>
              Potentiel de rendement à{" "}
              <span className="font-semibold">5 ans</span>
            </h2>
            <Chip
              label={`X${(simulatorData?.targetedYieldInPercent ?? 0) / 100}`}
              type="success"
            />
          </div>
        )}

        <div className="flex justify-between">
          <h2>Valorisation estimée de sortie</h2>
          <span className="font-semibold">
            {formatCurrency(Number(simulatorData?.targetedOutInCents))}
          </span>
        </div>
        <div className="mb-4 flex justify-between">
          <h2>Plus-value potentielle</h2>
          <span className="font-semibold">
            {formatCurrency(Number(simulatorData?.targetedGainInCents))}
          </span>
        </div>

        <SimulatorRoiBlock filters={simulatorForm} />
        <InvestButton className="mt-2" />
      </SimulatorResultsBlock>
    </div>
  );
}
